import {
    OrderResponseDto,
    OrderShippingGroupSelectionResponseDto,
} from 'client/widget-components/ecom/types/EcomSDK';
import { DataRow } from './components/DataCard';
import { EcommStr } from './useEcommStr';

export interface OrderShipment {
    /** Position in the order, 1-based. What the shopper reads, and what keys the card. */
    number: number;
    rows: DataRow[];
}

function deliveryEstimate(
    str: EcommStr,
    selection: OrderShippingGroupSelectionResponseDto
): string | undefined {
    const min = selection.min_delivery_time ?? selection.max_delivery_time;
    if (min === null) return undefined;

    const max =
        min === selection.max_delivery_time
            ? null
            : selection.max_delivery_time;

    return max !== null
        ? str('nativeEcommRuntime.shipping_rates.delivery_time_range', {
              min,
              max,
          })
        : str('nativeEcommRuntime.shipping_rates.delivery_time', {
              smart_count: min,
          });
}

/**
 * The shipment's number and the method that shipped it, as one phrase rather than a number
 * concatenated with a name: the separator between them is punctuation a locale decides, and a
 * translator cannot change one that lives here.
 *
 * Deliberately not the checkout's `shipping.shipment_label`, which names the shipping group in
 * `{1}` — a merchant-written name this page does not show, and one the checkout is dropping too.
 *
 * A selection the API synthesized from an order's scalar fields can carry no courier name, but it
 * is always alone and so never reaches this page.
 */
function label(
    str: EcommStr,
    selection: OrderShippingGroupSelectionResponseDto,
    n: number
): string {
    return str('nativeEcommRuntime.shipping.shipment_method_label', {
        number: n,
        method: selection.courier_name ?? '',
    });
}

/**
 * The selections in the order's own group order, default group first. `shipping_groups` and
 * `group_selections` are built by separate code paths, so numbering the shipments off the groups
 * is what makes them read as they were numbered at checkout. An order that reports no groups keeps
 * the selections in the order the API sent them.
 *
 * A selection no group claims still gets a card, appended after the ordered ones. The two lists
 * can disagree, and a shopper who paid for a shipment has to see it.
 */
function orderedSelections(
    order: OrderResponseDto
): OrderShippingGroupSelectionResponseDto[] {
    const selections = order.shipping_method?.group_selections ?? [];
    const groups = order.shipping_groups ?? [];

    if (groups.length === 0) return selections;

    const claimed = groups.flatMap((group) => {
        const selection = selections.find(
            (candidate) => candidate.shipping_group_id === group.id
        );
        return selection ? [selection] : [];
    });

    return [
        ...claimed,
        ...selections.filter((selection) => !claimed.includes(selection)),
    ];
}

/**
 * One card's worth of rows per shipment, for an order that shipped in several groups.
 *
 * Empty for every other order — one group, pickup, nothing shipped — which is what leaves those on
 * the single shipping-method card they have today. The count comes from the selections the order
 * carries and not from a feature flag: an order is a record of what happened, so it keeps its
 * shipments after the account stops shipping per group.
 */
export function getOrderShipments(
    str: EcommStr,
    order: OrderResponseDto
): OrderShipment[] {
    if (order.pickup) return [];

    const selections = orderedSelections(order);
    if (selections.length <= 1) return [];

    return selections.map((selection, index) => {
        const number = index + 1;
        const rows: DataRow[] = [
            { key: 'title', label: label(str, selection, number) },
        ];

        const estimate = deliveryEstimate(str, selection);
        if (estimate) {
            rows.push({ key: 'deliveryTime', value: estimate });
        }

        return { number, rows };
    });
}
